import { useEffect, useRef, useCallback, useState } from 'react'
import PropTypes from 'prop-types'

const VideoPlayer = ({ loop, muted, source, type, state, getVideoDuration, ...rest }) => {
  const videoElement = useRef(null)
  const videoSource = useRef(null)
  const [currentSource, setCurrentSource] = useState(source)

  const handleLoadedMetadata = useCallback(() => {
    if (videoElement.current) {
      getVideoDuration(videoElement.current.duration)
    }
  }, [getVideoDuration])

  useEffect(() => {
    const video = videoElement.current

    if (!video) return

    if (currentSource !== source) {
      videoSource.current.src = source
      video.load()
      setCurrentSource(source)
    }

    video.addEventListener('loadedmetadata', handleLoadedMetadata)

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [source, currentSource, handleLoadedMetadata])

  useEffect(() => {
    const video = videoElement.current
    if (!state || !video) return

    const { command, time } = state

    if (command === 'play' && video.paused) {
      const playPromise = video.play()
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          if (error.name !== 'AbortError') {
            console.error('Error al intentar reproducir el video:', error)
          }
        })
      }
    } else if (command === 'pause' && !video.paused) {
      video.pause()
    }

    if (time !== undefined && video.currentTime !== time) {
      video.currentTime = time
    }
  }, [state])

  useEffect(() => {
    const video = videoElement.current

    if (video && loop) {
      video.onended = () => {
        if (!video.paused) {
          video.currentTime = 0
          video.play()
        }
      }
    }

    return () => {
      if (video) {
        video.onended = null
      }
    }
  }, [loop])

  return (
    <video autoPlay preload='auto' loop={loop} muted={muted} ref={videoElement} {...rest}>
      <source ref={videoSource} src={currentSource} type={type} />
    </video>
  )
}

VideoPlayer.defaultProps = {
  loop: false,
  muted: true,
  getVideoDuration: () => {},
}

VideoPlayer.propTypes = {
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  source: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  state: PropTypes.object,
  getVideoDuration: PropTypes.func,
}

export default VideoPlayer
